//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ### MUI ###
import {
  Autocomplete,
  Box,
  Button,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  TextField,
} from '@mui/material';
import { useTheme, createTheme } from '@mui/material/styles';
import { Cancel, CheckCircle, Gavel, UnfoldMoreOutlined, CircleOutlined } from '@mui/icons-material';
import { blue, green, pink } from '@mui/material/colors';

// === UTILS ===
import { getHexAlpha } from 'components/styles.utils';

// === API ===
import { onDocumentRules, removeListener, requestDocumentRuleTasks } from 'api/prokey/prokey.websocket';

// === COMPONENTS ===

//#endregion

const RULE_COMPARE_METHODS = {
  status: (a, b) => {
    return (a || 0) - (b || 0);
  },
  description: (a, b) => {
    const cmp_a = (a || '').toUpperCase();
    const cmp_b = (b || '').toUpperCase();
    return cmp_a < cmp_b ? -1 : cmp_a > cmp_b ? 1 : 0;
  },
  comment: (a, b) => {
    const cmp_a = (a || '').toUpperCase();
    const cmp_b = (b || '').toUpperCase();
    return cmp_a < cmp_b ? -1 : cmp_a > cmp_b ? 1 : 0;
  },
};

//-----------------------------------------------------------------
export function Exceptions(props) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { type, onHover } = props;

  const theme    = useTheme   ();
  const navigate = useNavigate();

  const [ rules       , setRules      ] = useState( []       );
  const [ sort_header , setSortHeader ] = useState( 'status' );
  const [ sort_ascend , setSortAscend ] = useState( true     );
  const [ filters     , setFilters    ] = useState( []       );
  const [ document    , setDocument   ] = useState( ''       );   
  const has_filters    = filters.length > 0;
  const filtered_rules = has_filters ? rules.filter( file => filters.some( filter => filter(file) ) ) : rules;

  const _tags = [
    { label: 'Passed'      , group: 'Status' , filter: (rule) => rule.status ===  1 },
    { label: 'Failed'      , group: 'Status' , filter: (rule) => rule.status ===  0 },
    { label: 'Overruled'   , group: 'Status' , filter: (rule) => rule.status ===  2 },
    { label: 'Unprocessed' , group: 'Status' , filter: (rule) => rule.status ===  undefined },
  ];

  const [ defaultFilters , setDefaultFilters ] = useState( getFilterTags() );

  const _status_icons = {
    0 : <Cancel color='error'/>,
    1 : <CheckCircle color='success'/>,
    2 : <Gavel color='success'/>,
    3 : <CircleOutlined color='success'/>,
  }

  //#endregion

  //#region [STYLES]

  const theme_local = createTheme({
    status: {
      passed: green[300],
      failed: pink[300],
    },
    palette: {
      hover: blue[200],
    }
  });

  const _styles = {
    card: {
      height    : 'calc( 100% - 7rem )',
      width     : '40%',
      maxWidth  : '100%',
      overflowY : 'hidden',
      marginLeft: '1rem',
      border       : '1px solid ' + theme.palette.background.primary[theme.palette.mode],
      borderRadius : '1rem',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    control_container: {
      display: 'flex',
    },
    container_filter: {
      margin: '0 0.5rem',
    },
    filter_tags: {
      width: '28rem',
    },
    validate_button: {
      height: '4rem',
      margin: '0.5rem 0.5rem',
    },
    table_rules: {
    },
    table_header: {
      boxShadow : '0 0 0.5em 0.01em' + theme.palette.primary.main,
    },
    table_header_status: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor: sort_header === 'status' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_description: {
      cursor: 'pointer',
      backgroundColor: sort_header === 'description' ? theme.palette.primary.dark :theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_comment: {
      cursor: 'pointer',
      backgroundColor: sort_header === 'comment' ? theme.palette.primary.dark :theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_options: {
      backgroundColor: theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_container: {
      height    : '100%',
      width     : '100%',
      maxWidth  : '100%',
      overflowY : 'hidden',
    },
    table_container2: {
      height    : 'calc( 100% - 4rem )',
      width     : '100%',
      maxWidth  : '100%',
      padding   : '0',
      overflowY : 'auto',
      scrollbarColor: `${theme.palette.background.secondary[theme.palette.mode]} ${theme.palette.background.primary[theme.palette.mode]}`,
    },
    task_queue_text: {
      display      : 'flex',
      flexGrow     : 1,
      margin: '1rem',
      fontSize: 'x-large',
      borderColor: 'primary',
    },
    table_cell: {
      borderBottom: "none",
    },
    table_cell_status: {
      borderBottom: "none",
      textAlign: 'center',
    },
    hover: {
      "&:hover": {
        backgroundColor: theme_local.palette.hover + ' !important',
      },
    },
    passed: {
      backgroundColor: theme.palette.success.main + ' !important',
    },
    failed: {
      backgroundColor: theme.palette.error.main + ' !important',
    },
  }

  // === ATTRIBUTES

  const CONTROL_CONTAINER = {
    sx : _styles.control_container,
  }

  const FILTER_CONTAINER = {
    sx: _styles.container_filter,
  }

  const FILTER_TAGS = {
    id           : 'filter-tags',
    sx           : _styles.filter_tags,
    multiple     : true,
    options      : _tags,
    defaultValue : defaultFilters,
    groupBy      : (option) => option.group,
    renderInput  : (params) => { return <TextField {...params} variant='standard' label="Filter Tags" /> },
    onChange     : (event, value) => {
      setFilters         ( value.map( (x) => x.filter ) );
      saveSelectedFilters( value.map( (x) => x.label  ) );
    }, 
  }

  const VALIDATE_BUTTON = {
    sx: _styles.validate_button,
    variant  : 'contained',
    color    : 'success',
    onClick  : () => { onClick_Validate() },
    children : [ 'Review' ],
  }

  const TABLE_HEADER_STATUS = {
    sx            : _styles.table_header_status,
    sortDirection : sort_ascend ? 'asc' : 'desc',
    onClick       : () => onClickHeader( 'status' ),
    children      : <Box sx={{display: 'flex'}}><UnfoldMoreOutlined sx={{alignItem: 'center' }} /> Passed</Box>
  }

  const TABLE_HEADER_DESCRIPTION = {
    sx            : _styles.table_header_description,
    sortDirection : sort_ascend ? 'asc' : 'desc',
    onClick       : () => onClickHeader( 'description' ),
    children      : <Box sx={{display: 'flex'}}><UnfoldMoreOutlined sx={{alignItem: 'center' }} /> Description</Box>
  }

  const TABLE_HEADER_COMMENT = {
    sx            : _styles.table_header_comment,
    sortDirection : sort_ascend ? 'asc' : 'desc',
    onClick       : () => onClickHeader( 'comment' ),
    children      : <Box sx={{display: 'flex'}}><UnfoldMoreOutlined sx={{alignItem: 'center' }} /> Comments</Box>
  }

  const TABLE_HEADER_OPTIONS = {
    sx            : _styles.table_header_options,
    sortDirection : sort_ascend ? 'asc' : 'desc',
    children      : [ 'Options' ],
  }

  //#endregion

  //#region [EFFECT]

  useEffect(() => {
    //-----------------------------------------------------------------
    function listener_document_rules(data) {
      //-----------------------------------------------------------------
      const doc_rules = data.rules;
      doc_rules.sort( (a,b) => RULE_COMPARE_METHODS[sort_header]( a[sort_header], b[sort_header] ) );
      if( !sort_ascend ) doc_rules.reverse();
      setRules( doc_rules );
      setDocument( data.numcode );
    }

    onDocumentRules( listener_document_rules );

    return () => {
      removeListener( 'DOCUMENT-RULES' , listener_document_rules );
    };
  }, []); // empty dependency = called only once at startup
  
  useEffect(() => {
    const tags = getFilterTags();

    setDefaultFilters( tags );
    setFilters( tags.map( tag => tag.filter ) );
  }, [rules] );

  //#endregion
  
  //#region [METHOD]

  //-----------------------------------------------------------------
  function getFilterTags() {
    //-----------------------------------------------------------------
    let result = [];

    try {
      const saved_filters = loadSelectedFilters();
      result = _tags.filter( tag => saved_filters?.includes( tag.label ) );
    } catch (error) {
      console.warn( error );
    }
  
    return result;
  }

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onClickHeader( name ) {
    //-----------------------------------------------------------------
    const sorted = [...rules].sort( (a,b) => RULE_COMPARE_METHODS[name]( a[name], b[name] ) );

    if( sort_header === name ) {
      setSortAscend( !sort_ascend );
      if( sort_ascend ) sorted.reverse();
    } else {
      setSortAscend( true );
      setSortHeader( name );
    }

    setRules(sorted);
  }

  //-----------------------------------------------------------------
  function onClick_Validate( ) {
    //-----------------------------------------------------------------
    navigate( `task?document=${document}` );
  }

   //-----------------------------------------------------------------
   function saveSelectedFilters(filterLabels) {
    //-----------------------------------------------------------------    
    localStorage.setItem('da-rules-filters', JSON.stringify(filterLabels));
  }

  //-----------------------------------------------------------------
  function loadSelectedFilters() {
    //-----------------------------------------------------------------
    const savedFilters = localStorage.getItem('da-rules-filters');    
      return JSON.parse(savedFilters);    
  }

  //#endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  function displayData() {
    //-----------------------------------------------------------------
    return (
      <Box sx={_styles.card}>
        <TableContainer sx={_styles.table_container}>
          <Box { ...CONTROL_CONTAINER }>
            <Typography sx={_styles.task_queue_text}>
              Data Rules
            </Typography>
            <Box { ...FILTER_CONTAINER }>
              <Autocomplete { ...FILTER_TAGS }/>
            </Box>
            <Button { ...VALIDATE_BUTTON } />
          </Box>
          <TableContainer sx={_styles.table_container2}>
            <Table stickyHeader size='small' sx={_styles.table_rules}>
              <TableHead sx={_styles.table_header}>
                <TableRow>
                  <TableCell key={1} { ...TABLE_HEADER_STATUS      } />
                  <TableCell key={2} { ...TABLE_HEADER_DESCRIPTION } />
                </TableRow>
              </TableHead>
              <TableBody>
                {displayDataItems()}
              </TableBody>
            </Table>
          </TableContainer>
        </TableContainer>
      </Box>
    )
  }

  //-----------------------------------------------------------------
  function displayDataItems() {
    //-----------------------------------------------------------------
    return filtered_rules.map( item => {
      return (
        <TableRow hover classes={{hover: _styles.hover,}} key={item.id} id={item.id} onMouseOver={onHover}>
          <TableCell key={1} sx={_styles.table_cell_status}>
            {item.review ? <div /> : _status_icons[item.status] || <div />}
          </TableCell>
          <TableCell key={2} sx={_styles.table_cell}> { item.description  } </TableCell>
        </TableRow>
      )
    });
  };

  //#endregion

  return type === 'DOCUMENT' ? null : displayData();
}